/**
 * WAF API Protection (Owl/Mlytics). Service-scoped and domain-anchored (`?domain=`).
 * A rule is an ordered list (by priority) of match `conditions` + a single
 * `apiProtection` action that signs an access token on a request parameter and
 * (optionally) rate-limits matched API requests.
 *
 * Backed by `modules/service/waf/controller_api_protection.go`:
 *   - GET    /services/:service_id/waf/api-protection/rules            → a domain's rules (sorted by priority)
 *   - POST   /services/:service_id/waf/api-protection/rules            → create a rule
 *   - PATCH  /services/:service_id/waf/api-protection/rules/:id         → replace a rule (full body)
 *   - PATCH  /services/:service_id/waf/api-protection/rules/:id/enable  → toggle a rule on/off
 *   - DELETE /services/:service_id/waf/api-protection/rules/:id         → delete a rule
 *
 * Conditions reuse the `ATPCondition` shape (single `value`, no `values[]`/logic)
 * and the firewall field/operator vocabulary, so the builder is driven by
 * `useWafFirewallSchema` (no dedicated API-protection schema call).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';
import type { ATPCondition } from './account-takeover';

/** The `apiProtection` action — token signing + optional per-rule rate limit. */
export interface APIProtectionAction {
  type: string;                   // "apiProtection"
  param: string;                  // token param name (e.g. "token")
  key: string;                    // shared signing secret
  token_ttl: number;              // token validity, seconds
  rate_limit_enable: boolean;     // per-rule rate limit toggle
  rate_limit_queries: number;     // allowed queries per window
  rate_limit_block_time: number;  // block duration, seconds
}

/** A stored API-protection rule (`GET /waf/api-protection/rules`, sorted by priority). */
export interface APIProtectionRule {
  id: string;
  rule_id: number;
  name: string;
  enable: boolean;
  priority: number;
  type: string;
  action: APIProtectionAction;
  conditions: ATPCondition[];
  created_at?: number;
  updated_at?: number;
}

/** Create/update body (full replace on update). */
export interface APIProtectionRuleInput {
  name: string;
  enable?: boolean;
  action: APIProtectionAction;
  conditions: ATPCondition[];
}

/** A domain's API-protection rules (sorted by priority). */
export function useWafApiProtectionRules(serviceId: string | undefined, domain: string | undefined) {
  return useQuery<APIProtectionRule[]>({
    queryKey: wafKeys.apiProtectionRules(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<APIProtectionRule[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/api-protection/rules`, { params: { domain } }),
      ) ?? [],
  });
}

function invalidateRules(qc: ReturnType<typeof useQueryClient>, serviceId?: string) {
  if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'api-protection-rules'] });
}

/** Create an API-protection rule (`POST /waf/api-protection/rules`). */
export function useCreateApiProtectionRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; input: APIProtectionRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: APIProtectionRuleInput }) =>
      unwrap(
        await serviceApiV3.post(`/services/${serviceId}/waf/api-protection/rules`, input, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Replace an API-protection rule (`PATCH /waf/api-protection/rules/:id`, full body). */
export function useUpdateApiProtectionRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: string; input: APIProtectionRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, input }: { domain: string; id: string; input: APIProtectionRuleInput }) =>
      unwrap(
        await serviceApiV3.patch(`/services/${serviceId}/waf/api-protection/rules/${id}`, input, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Toggle an API-protection rule on/off (`PATCH /waf/api-protection/rules/:id/enable`). */
export function useSetApiProtectionRuleEnabled(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: string; enable: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, enable }: { domain: string; id: string; enable: boolean }) =>
      unwrap(
        await serviceApiV3.patch(`/services/${serviceId}/waf/api-protection/rules/${id}/enable`, { enable }, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Delete an API-protection rule (`DELETE /waf/api-protection/rules/:id`). */
export function useDeleteApiProtectionRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { domain: string; id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id }: { domain: string; id: string }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/api-protection/rules/${id}`, { params: { domain } });
    },
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}
